import { useCallback, useEffect, useState } from 'react';

import { useDevice } from '@/resources/hooks/useDevice';
import { useOrderContext } from '@/resources/hooks/useOrderContext';
import { useWebContext } from '@/resources/hooks/useWebContext';
import { getProductByCombo } from '@/routes/products';
import { IBestCombo, ICartCombo } from '@/store/ducks/combos/types';
import { ICartProduct } from '@/store/ducks/order/types';
import { IFullProduct, IProduct } from '@/store/ducks/products/types';

export const useCartModals = () => {
  const { setFunction } = useOrderContext();
  const { setMenuOpen } = useWebContext();
  const { device } = useDevice();

  const [comboBottomSheetOpen, setComboBottomSheetOpen] = useState<boolean>(false);
  const [comboModalOpen, setComboModalOpen] = useState<boolean>(false);
  const [showComboAddModal, setShowComboAddModal] = useState(false);
  const [comboRemoveItemModalOpen, setComboRemoveItemModalOpen] = useState(false);
  const [modalAddProduct, setModalAddProduct] = useState<IProduct | undefined>(undefined);
  const [modalRemoveProduct, setModalRemoveProduct] = useState<IProduct | undefined>(undefined);
  const [addProductTrigger, setAddProductTrigger] = useState(0);
  const [removeProductTrigger, setRemoveProductTrigger] = useState(0);

  const openBottomSheet = useCallback(() => {
    setMenuOpen(true);
    setComboBottomSheetOpen(true);
  }, [setMenuOpen]);

  const handleSetModalAddProduct = useCallback((product: IProduct | undefined) => {
    setModalAddProduct(product);
    setAddProductTrigger((prev) => prev + 1);
  }, []);

  const handleSetModalRemoveProduct = useCallback((product: IProduct | undefined) => {
    setModalRemoveProduct(product);
    setRemoveProductTrigger((prev) => prev + 1);
  }, []);

  const openComboDetails = useCallback(
    () => (device === 'desktop' ? setComboModalOpen(true) : openBottomSheet()),
    [device, openBottomSheet],
  );

  useEffect(() => {
    setShowComboAddModal(!!modalAddProduct);
  }, [addProductTrigger]);

  useEffect(() => {
    setComboRemoveItemModalOpen(!!modalRemoveProduct);
  }, [removeProductTrigger]);

  useEffect(() => {
    setFunction('setModalAddProduct', handleSetModalAddProduct);
    setFunction('setModalRemoveProduct', handleSetModalRemoveProduct);
    setFunction('openComboAddModal', () => setShowComboAddModal(true));
    setFunction('closeComboAddModal', () => setShowComboAddModal(false));
    setFunction('openComboRemoveItemModal', () => setComboRemoveItemModalOpen(true));
    setFunction('openComboDetails', openComboDetails);
  }, [handleSetModalAddProduct, handleSetModalRemoveProduct, openComboDetails, setFunction]);

  const closeComboModal = useCallback(() => {
    setComboModalOpen(false);
  }, []);

  const closeComboBottomSheet = useCallback(() => {
    setComboBottomSheetOpen(false);
    setMenuOpen(false);
  }, [setMenuOpen]);

  const closeComboAddModal = useCallback(() => {
    setShowComboAddModal(false);
    setModalAddProduct(undefined);
  }, []);

  const closeComboRemoveModal = useCallback(() => {
    setComboRemoveItemModalOpen(false);
    setModalRemoveProduct(undefined);
  }, []);

  return {
    comboBottomSheetOpen,
    setComboBottomSheetOpen,
    comboModalOpen,
    setComboModalOpen,
    showComboAddModal,
    setShowComboAddModal,
    comboRemoveItemModalOpen,
    setComboRemoveItemModalOpen,
    modalAddProduct,
    modalRemoveProduct,
    openComboDetails,
    closeComboModal,
    closeComboBottomSheet,
    closeComboAddModal,
    closeComboRemoveModal,
  };
};

export const useCartData = () => {
  const { setFunction } = useOrderContext();
  const { order } = useWebContext();

  const [combos, setCombos] = useState<ICartCombo[] | []>([]);
  const [productVariants, setProductVariants] = useState<Record<string, IFullProduct[]>>({});
  const [productToCombo, setProductToCombo] = useState<{ best_combo: IBestCombo }>();
  const [editProducts, setEditProducts] = useState<IProduct[] | ICartProduct[]>();

  useEffect(() => {
    if (order?.cart) {
      setCombos(order?.cart.combos);
    }
  }, [order?.cart]);

  useEffect(() => {
    setFunction('setEditProducts', setEditProducts);
    setFunction('setProductToCombo', setProductToCombo);
  }, [setFunction]);

  useEffect(() => {
    if (productToCombo?.best_combo) {
      getProductByCombo(order?.shop_id, productToCombo.best_combo.id).then((products) => {
        if (!products || products.length === 0) return;
        products.map((product: IProduct) =>
          setProductVariants((prev) => ({
            ...prev,
            [product.product_id]: product.full_products,
          })),
        );
      });
    }
  }, [productToCombo, order?.shop_id]);

  const clearProductVariants = useCallback(() => {
    setProductVariants({});
  }, []);

  return {
    combos,
    productVariants,
    productToCombo,
    editProducts,
    setProductToCombo,
    setEditProducts,
    clearProductVariants,
  };
};
